import type { PersonId } from '@shared/brand';
import type { RequestApproval } from '../entities/VacationRequest';
import { collectedSignatures } from '../entities/VacationRequest';
import type { RequestStatus, Role } from '../vocabulary';
import type { CalendarDate } from '../value-objects/CalendarDate';
import type { DateRange } from '../value-objects/DateRange';

/**
 * Who has to sign, and what the request becomes when they do.
 *
 * The handoff's rule: when a person reports to two or more leaders and the
 * `doblevb` feature is on, the request needs two signatures. The first moves it
 * to "Visto bueno"; the second to "Aprobada". With one leader, or the feature
 * off, a single signature approves outright.
 *
 * The signature requirement is decided once, at submission, and frozen on the
 * request. Recomputing it on each decision would mean that switching the feature
 * off — or reassigning a leader — could leave a request that already has one
 * signature suddenly "approved", or a fully approved one suddenly short. Freezing
 * it makes the flow of any given request stable regardless of later
 * configuration changes.
 */

export type SignatureCount = 1 | 2;

/** Signatures a new request will need. Pure function of the inputs shown. */
export const requiredSignaturesFor = (params: {
  readonly leaderCount: number;
  readonly doubleSignOffEnabled: boolean;
}): SignatureCount => (params.doubleSignOffEnabled && params.leaderCount >= 2 ? 2 : 1);

export type ApprovalRejection =
  | { readonly kind: 'not-an-approver' }
  | { readonly kind: 'already-decided'; readonly status: RequestStatus }
  | { readonly kind: 'already-signed' }
  | { readonly kind: 'reason-required' }
  | { readonly kind: 'no-pending-slot' };

export interface ApprovalOutcome {
  readonly nextStatus: RequestStatus;
  /** Position this signature takes in the chain. */
  readonly order: SignatureCount;
  /** True when this signature completes the request. */
  readonly isFinal: boolean;
}

/**
 * Los roles que hacen de líder suplente cuando alguien no tiene ninguno.
 *
 * Espejo de `STANDIN_APPROVER_ROLES` en el servidor, que es donde de verdad se
 * decide. `global` no está: mira las ocho áreas y no firma en ninguna.
 */
export const standsInForMissingLeader = (role: Role): boolean =>
  role === 'admin' || role === 'super';

/**
 * Los roles que firman aunque la persona sí tenga líderes.
 *
 * Sólo el superusuario. Espejo de `OVERRIDE_APPROVER_ROLES`; el permiso `aprobar`
 * lo comprueba aparte quien llama, y sin él esto no se pregunta.
 */
export const signsOverLeaders = (role: Role): boolean => role === 'super';

/**
 * Quién puede firmar esta solicitud.
 *
 * Tres reglas, en orden de rareza:
 *
 *  1. **Sus líderes.** Lo normal, y lo único mientras haya alguno.
 *  2. **Sin ninguno, quien administra el área.** Si no, la solicitud no la puede
 *     aprobar nadie y espera para siempre; era el estado real de 38 personas.
 *  3. **El superusuario, siempre.** El comodín para lo que no sale de otra forma
 *     —una solicitud congelada pidiendo dos firmas cuya persona ya sólo tiene una
 *     líder, que ya firmó—. No es la regla; es la salida.
 *
 * Y por encima de las tres: **nadie firma lo suyo**. Se filtra al final para que
 * ninguna de ellas pueda saltárselo.
 */
export const approversFor = (params: {
  readonly assignedLeaderIds: readonly PersonId[];
  readonly requesterId: PersonId;
  /** Firman sólo si la persona no tiene ningún líder. */
  readonly standInIds: readonly PersonId[];
  /** Firman aunque los tenga. */
  readonly overrideIds: readonly PersonId[];
}): readonly PersonId[] => {
  const base =
    params.assignedLeaderIds.length > 0 ? params.assignedLeaderIds : params.standInIds;

  return [...new Set([...base, ...params.overrideIds])].filter(
    (id) => id !== params.requesterId,
  );
};

/**
 * Whether `approverId` may act on this request at all. Membership in the
 * request's own leader list, not a role check: being a leader somewhere does not
 * make you a leader of this person.
 */
export const canDecide = (params: {
  readonly approverId: PersonId;
  readonly assignedLeaderIds: readonly PersonId[];
  readonly status: RequestStatus;
  readonly existingApprovals: readonly RequestApproval[];
}): boolean => {
  if (!params.assignedLeaderIds.includes(params.approverId)) return false;
  if (isDecidedStatus(params.status)) return false;
  // One signature per person: a leader cannot supply both halves of a double
  // sign-off, which would defeat the point of requiring two.
  return !params.existingApprovals.some(
    (a) => a.approverPersonId === params.approverId && a.decision !== 'pending',
  );
};

const isDecidedStatus = (status: RequestStatus): boolean =>
  status === 'Aprobada' ||
  status === 'Rechazada' ||
  status === 'Cancelada' ||
  status === 'Tomada';

/**
 * Applies an approval and reports the resulting status.
 *
 * Returns a rejection rather than throwing, so the callable can turn it into a
 * precise message: "ya la firmó alguien más" reads very differently from "no eres
 * su líder".
 */
export const applyApproval = (params: {
  readonly approverId: PersonId;
  readonly assignedLeaderIds: readonly PersonId[];
  readonly status: RequestStatus;
  readonly requiredSignatures: SignatureCount;
  readonly existingApprovals: readonly RequestApproval[];
}):
  | { readonly ok: true; readonly outcome: ApprovalOutcome }
  | { readonly ok: false; readonly error: ApprovalRejection } => {
  if (isDecidedStatus(params.status)) {
    return { ok: false, error: { kind: 'already-decided', status: params.status } };
  }
  if (!params.assignedLeaderIds.includes(params.approverId)) {
    return { ok: false, error: { kind: 'not-an-approver' } };
  }
  if (
    params.existingApprovals.some(
      (a) => a.approverPersonId === params.approverId && a.decision !== 'pending',
    )
  ) {
    return { ok: false, error: { kind: 'already-signed' } };
  }

  const signaturesSoFar = collectedSignatures(params.existingApprovals).length;
  if (signaturesSoFar >= params.requiredSignatures) {
    return { ok: false, error: { kind: 'no-pending-slot' } };
  }

  const order = (signaturesSoFar + 1) as SignatureCount;
  const isFinal = order >= params.requiredSignatures;

  return {
    ok: true,
    outcome: {
      order,
      isFinal,
      nextStatus: isFinal ? 'Aprobada' : 'Visto bueno',
    },
  };
};

/**
 * Applies a rejection. A single rejection ends the request regardless of how
 * many signatures it needed — there is no "two leaders must both refuse".
 *
 * The reason is mandatory. The handoff requires it, and it is what makes the
 * audit log and the reschedule flow work: the employee is shown why, and the
 * system suggests alternative windows off the back of it.
 */
export const applyRejection = (params: {
  readonly approverId: PersonId;
  readonly assignedLeaderIds: readonly PersonId[];
  readonly status: RequestStatus;
  readonly existingApprovals: readonly RequestApproval[];
  readonly reason: string;
}):
  | { readonly ok: true; readonly outcome: ApprovalOutcome }
  | { readonly ok: false; readonly error: ApprovalRejection } => {
  if (params.reason.trim().length === 0) {
    return { ok: false, error: { kind: 'reason-required' } };
  }
  if (isDecidedStatus(params.status)) {
    return { ok: false, error: { kind: 'already-decided', status: params.status } };
  }
  if (!params.assignedLeaderIds.includes(params.approverId)) {
    return { ok: false, error: { kind: 'not-an-approver' } };
  }
  if (
    params.existingApprovals.some(
      (a) => a.approverPersonId === params.approverId && a.decision !== 'pending',
    )
  ) {
    return { ok: false, error: { kind: 'already-signed' } };
  }

  const order = (collectedSignatures(params.existingApprovals).length + 1) as SignatureCount;

  return {
    ok: true,
    outcome: { order, isFinal: true, nextStatus: 'Rechazada' },
  };
};

/**
 * The signature trail the design draws as segments on each card: one segment per
 * required signature, filled in chain order.
 */
export interface SignatureSegment {
  readonly order: SignatureCount;
  readonly signed: boolean;
  readonly approverName: string | null;
  /**
   * Quién firmó, por id. `null` mientras el hueco esté vacío.
   *
   * Hace falta para saber **a quién le toca el hueco siguiente**: el tablero lo
   * rellenaba con el líder que ocupara esa posición en la lista, sin mirar quién
   * había firmado ya, y cuando el primer firmante venía segundo en esa lista se
   * pintaba su nombre en los dos renglones. Comparar por nombre habría arreglado
   * el caso visible y roto el de dos personas homónimas.
   */
  readonly approverPersonId: PersonId | null;
  readonly rejected: boolean;
}

export const signatureTrail = (
  requiredSignatures: SignatureCount,
  approvals: readonly RequestApproval[],
): readonly SignatureSegment[] => {
  const segments: SignatureSegment[] = [];
  for (let order = 1; order <= requiredSignatures; order += 1) {
    const match = approvals.find((a) => a.order === order && a.decision !== 'pending');
    segments.push({
      order: order as SignatureCount,
      signed: match?.decision === 'approved',
      rejected: match?.decision === 'rejected',
      approverName: match?.approverName ?? null,
      approverPersonId: match?.approverPersonId ?? null,
    });
  }
  return segments;
};

/**
 * A quién le toca cada hueco del rastro que todavía nadie ha firmado.
 *
 * El tablero enseña dos renglones cuando hacen falta dos firmas, y el segundo
 * está vacío hasta que alguien lo firma. Rellenarlo con «el líder que ocupe esa
 * posición en la lista» parecía suficiente y no lo era: el orden de los vínculos
 * no tiene nada que ver con el de las firmas, así que cuando el primer firmante
 * venía segundo en la lista, su nombre aparecía en los dos renglones.
 *
 * Pasó en producción con una solicitud cuyos dos líderes son «María Guadalupe
 * Carranza» y «Mariana Zambrano»: se leía dos veces el mismo nombre y parecía un
 * duplicado de datos cuando los datos estaban bien.
 *
 * Se reparte por identidad y no por posición: quien ya firmó no vuelve a
 * proponerse, y los que quedan se asignan a los huecos en orden. Devuelve un id
 * por segmento, o `null` cuando ya no queda nadie a quien esperar.
 */
export const pendingSignersFor = (
  trail: readonly SignatureSegment[],
  leaderIds: readonly PersonId[],
): readonly (PersonId | null)[] => {
  const yaFirmaron = new Set(
    trail.map((segment) => segment.approverPersonId).filter((id): id is PersonId => id !== null),
  );
  const disponibles = leaderIds.filter((id) => !yaFirmaron.has(id));

  let siguiente = 0;
  return trail.map((segment) =>
    segment.approverPersonId ?? (disponibles[siguiente++] ?? null),
  );
};

/** The status a freshly submitted request starts in. */
export const initialStatus = (): RequestStatus => 'Por revisar';

/**
 * The status a request moves to when a leader picks it up without yet deciding —
 * the kanban board's "En revisión" column. A presentational transition with no
 * business consequence, which is why dragging a card there is allowed to be a
 * plain status write rather than a decision.
 */
export const isReviewTransitionAllowed = (from: RequestStatus, to: RequestStatus): boolean => {
  if (isDecidedStatus(from)) return false;
  return to === 'Por revisar' || to === 'En revisión';
};

/**
 * Where an approval will land the request, without asking the server.
 *
 * The server's rule is `signaturesSoFar + 1 >= requiredSignatures ? 'Aprobada' :
 * 'Visto bueno'` (`decide.ts`), and the board can derive `signaturesSoFar` from
 * the column the card is in: «Visto bueno» *means* one signature is already in.
 * That equivalence is what lets the card move the instant it is dropped instead
 * of after a round trip, and it is the reason this lives here rather than being
 * guessed in the screen — if the transition ever changes, it changes in one
 * place and this test fails.
 *
 * Deliberately not a promise: the server still decides, and a request that
 * gained its second signature in another tab lands somewhere else. The board
 * reconciles when the refetch arrives; this only has to be right often enough
 * that the correction is rare.
 */
export const statusAfterApproval = (request: {
  readonly status: RequestStatus;
  readonly requiredSignatures: SignatureCount;
}): RequestStatus => {
  if (request.requiredSignatures === 1) return 'Aprobada';
  return request.status === 'Visto bueno' ? 'Aprobada' : 'Visto bueno';
};

/**
 * Una solicitud que se quedó sin decisión y a la que ya le llegaron las fechas.
 *
 * Mientras nadie la decide, sus días cuentan como apartados. Si además el primer
 * día pedido ya pasó, la pregunta «¿aprobar o rechazar?» dejó de tener sentido: la
 * única que queda es si esa persona se fue o no, y ésa la contesta quien la iba a
 * firmar. Dos así aparecieron en Tecnología el 18 sep 2026, una enviada dos
 * semanas antes.
 *
 * **Se mide desde el primer día y no desde el último.** Esperar a que termine el
 * rango deja pasar días en los que el líder todavía podía aprobar a tiempo, y
 * quien pidió un puente de cinco días se quedaría cuatro sin saber si se va.
 */
export const isOverdueUndecided = (
  request: { readonly status: RequestStatus; readonly range: DateRange },
  today: CalendarDate,
): boolean =>
  (request.status === 'Por revisar' ||
    request.status === 'En revisión' ||
    request.status === 'Visto bueno') &&
  request.range.start.isBefore(today);
